import React, { useState } from 'react'
import styles from './ContactSection.module.css'
import { useLang } from '../../i18n/LanguageContext'

const INITIAL = { name: '', company: '', email: '', phone: '', message: '' }

export default function ContactSection() {
  const { T } = useLang()
  const [form, setForm] = useState(INITIAL)
  const [status, setStatus] = useState('idle')

  const handleChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }))

  const handleSubmit = async (e) => {
    e.preventDefault()
    setStatus('sending')
    try {
      const res = await fetch('/.netlify/functions/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      })
      if (!res.ok) throw new Error('Send failed')
      setStatus('success')
      setForm(INITIAL)
    } catch {
      setStatus('error')
    }
  }

  const f = T.contact.form
  const info = T.contact.info

  return (
    <section className={`section ${styles.contact}`} id="contato">
      <div className="container">
        <div className={styles.layout}>
          {/* Left: form */}
          <div className={styles.formCol}>
            <p className="section-label">{T.contact.label}</p>
            <h2 className={`section-title section-title--light ${styles.title}`}>
              {T.contact.heading1}
              <br />
              {T.contact.heading2}
            </h2>
            <p className="section-desc section-desc--light">{T.contact.desc}</p>

            {status === 'success' ? (
              <div className={styles.success}>
                <span className={styles.successIcon}>✓</span>
                <div>
                  <strong>{T.contact.success.title}</strong>
                  <p>{T.contact.success.desc}</p>
                </div>
              </div>
            ) : (
              <form className={styles.form} onSubmit={handleSubmit}>
                <div className={styles.row}>
                  <div className={styles.field}>
                    <label htmlFor="name" className={styles.label}>{f.name}</label>
                    <input id="name" name="name" type="text" required value={form.name} onChange={handleChange} className={styles.input} placeholder={f.namePlaceholder} />
                  </div>
                  <div className={styles.field}>
                    <label htmlFor="company" className={styles.label}>{f.company}</label>
                    <input id="company" name="company" type="text" required value={form.company} onChange={handleChange} className={styles.input} placeholder={f.companyPlaceholder} />
                  </div>
                </div>
                <div className={styles.row}>
                  <div className={styles.field}>
                    <label htmlFor="email" className={styles.label}>{f.email}</label>
                    <input id="email" name="email" type="email" required value={form.email} onChange={handleChange} className={styles.input} placeholder={f.emailPlaceholder} />
                  </div>
                  <div className={styles.field}>
                    <label htmlFor="phone" className={styles.label}>{f.phone}</label>
                    <input id="phone" name="phone" type="tel" value={form.phone} onChange={handleChange} className={styles.input} placeholder={f.phonePlaceholder} />
                  </div>
                </div>
                <div className={styles.field}>
                  <label htmlFor="message" className={styles.label}>{f.message}</label>
                  <textarea id="message" name="message" required rows={4} value={form.message} onChange={handleChange} className={`${styles.input} ${styles.textarea}`} placeholder={f.messagePlaceholder} />
                </div>
                <button type="submit" className={`btn btn-primary ${styles.submit}`} disabled={status === 'sending'}>
                  {status === 'sending' ? f.sending : f.submit}
                </button>
                {status === 'error' && <p className={styles.errorMsg}>{f.error}</p>}
              </form>
            )}
          </div>

          {/* Right: contact info */}
          <div className={styles.infoCol}>
            <div className={styles.infoCard}>
              <h3 className={styles.infoTitle}>{info.title}</h3>

              {/* WhatsApp */}
              <a href="https://wa.me/5511988049376" target="_blank" rel="noopener noreferrer" className={styles.infoLink}>
                <div className={styles.infoIcon} aria-hidden="true">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.27h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.91a16 16 0 0 0 6.18 6.18l.97-.97a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
                  </svg>
                </div>
                <div>
                  <div className={styles.infoLabel}>{info.whatsapp}</div>
                  <div className={styles.infoValue}>(11) 98804-9376</div>
                </div>
              </a>

              {/* Email */}
              <a href="mailto:comercial@eqpitech.com.br" className={styles.infoLink}>
                <div className={styles.infoIcon} aria-hidden="true">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="4" width="20" height="16" rx="2"/>
                    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
                  </svg>
                </div>
                <div>
                  <div className={styles.infoLabel}>{info.email}</div>
                  <div className={styles.infoValue}>comercial@eqpitech.com.br</div>
                </div>
              </a>

              {/* Phone Portugal */}
              <a href="tel:+351227313256" className={styles.infoLink}>
                <div className={styles.infoIcon} aria-hidden="true">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.27h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.91a16 16 0 0 0 6.18 6.18l.97-.97a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
                  </svg>
                </div>
                <div>
                  <div className={styles.infoLabel}>{info.phonePT}</div>
                  <div className={styles.infoValue}>+351 227 313 256</div>
                </div>
              </a>

              <div className={styles.divider} />

              <div className={styles.supportBlock}>
                <p className={styles.supportTitle}>{info.support}</p>
                <a href="mailto:suporte@equipoinfo.com.br" className={styles.supportLink}>suporte@equipoinfo.com.br</a>
                <a href="tel:+551123053881" className={styles.supportLink}>+55 11 2305-3881</a>
              </div>

              <div className={styles.divider} />

              <a href="https://www.instagram.com/eqpi.tech" target="_blank" rel="noopener noreferrer" className={styles.social}>
                {info.instagram}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
